/**
 * Student authentication service.
 *
 * The single source of student login logic. Previously the ~100-line login
 * flow (lockout check → storage init → student lookup → PIN setup/verify →
 * rate-limiting → new-student creation) was duplicated verbatim in
 * `qd-login.ts` across `handleStudentLogin` and `retryLoginAfterMigration`.
 * Both paths now delegate to {@link AuthService}, which performs all storage,
 * crypto, and rate-limit calls and returns a discriminated {@link LoginResult}.
 *
 * The component is left to translate the result into session creation, custom
 * events, and UI state — it performs no storage/crypto/rate-limit work itself
 * (contracts/module-boundaries.md → Auth).
 */

import { SCHEMA_VERSION } from '../../types/contracts.js';
import type { StudentRecord } from '../../types/contracts.js';
import { getStorageAdapter } from '../storage/indexeddb.js';
import {
  needsMigration,
  hasPinSet,
  completePinSetup,
  upgradePinCredential,
} from '../storage/migration.js';
import { createPinCredential, verifyStoredPin } from './pin-service.js';
import {
  checkLockout,
  recordFailedAttempt,
  clearAttemptState,
  getRemainingAttempts,
} from './rate-limiter.js';
import { StorageFormatError } from '../storage/adapter-utils.js';

/**
 * Discriminated result of a student login attempt.
 *
 * - `pin-created` — a new PIN was stored (new student, migrated student, or a
 *   student who had no PIN). The caller should surface the "PIN stored"
 *   confirmation and complete the login.
 * - `pin-verified` — an existing student's PIN matched. The caller completes
 *   the login without a confirmation dialog.
 * - `lockout` — the account is (or just became) locked; `lockoutMs` is the
 *   remaining lockout window in milliseconds.
 * - `bad-pin` — the PIN was wrong but the account is not yet locked;
 *   `remaining` is the number of attempts left.
 * - `needs-migration` — stored data is in an incompatible format and must be
 *   migrated before login can proceed.
 * - `error` — login failed for another reason; `message` is user-facing.
 */
export type LoginResult =
  | { kind: 'pin-created'; serviceId: string; name: string; release: string }
  | { kind: 'pin-verified'; serviceId: string; name: string; release: string }
  | { kind: 'lockout'; lockoutMs: number }
  | { kind: 'bad-pin'; remaining: number }
  | { kind: 'needs-migration'; error: StorageFormatError }
  | { kind: 'error'; message: string };

/**
 * Inputs required to authenticate a student.
 */
export interface StudentLoginInput {
  serviceId: string;
  name: string;
  pin: string;
  release: string;
  dbName: string;
}

interface RunOptions {
  /** Whether to enforce the lockout window before attempting login. */
  checkLock: boolean;
  /** Surface a {@link StorageFormatError} as `needs-migration` (initial login only). */
  surfaceMigration: boolean;
  /** User-facing message used when an unexpected error occurs. */
  errorMessage: string;
  /** Console label used when logging an unexpected error. */
  errorLabel: string;
}

/**
 * Service encapsulating all student authentication logic.
 */
export class AuthService {
  /**
   * Authenticate a student (initial login path).
   *
   * Enforces the lockout window and surfaces storage-format mismatches as
   * `needs-migration` so the caller can offer migration.
   */
  loginStudent(input: StudentLoginInput): Promise<LoginResult> {
    return this.runLogin(input, {
      checkLock: true,
      surfaceMigration: true,
      errorMessage: 'Login failed. Please try again.',
      errorLabel: 'Student login error:',
    });
  }

  /**
   * Authenticate a student immediately after a successful data migration.
   *
   * Skips the lockout pre-check (the user just completed migration) and treats
   * any remaining error as a generic post-migration failure.
   */
  retryAfterMigration(input: StudentLoginInput): Promise<LoginResult> {
    return this.runLogin(input, {
      checkLock: false,
      surfaceMigration: false,
      errorMessage: 'Login failed after migration. Please try again.',
      errorLabel: 'Post-migration login error:',
    });
  }

  /**
   * Whether a service ID already has an account with a usable PIN.
   *
   * Used by the login form to label its submit button "Login" or "Create", so
   * a first-time user is told they are about to create an account rather than
   * guessing why their credentials are not accepted. Accounts are keyed on
   * `{release, serviceId}`, so the name is not part of this check.
   *
   * Never throws: any storage problem resolves to `null` ("unknown"), leaving
   * the form to fall back to its neutral label rather than blocking the user.
   *
   * @param serviceId - Service ID being entered
   * @param release - Release the account would belong to
   * @param dbName - IndexedDB database name from page config
   * @returns true when a PIN-bearing record exists, false when it does not,
   *   null when the lookup could not be completed
   */
  async isRegistered(serviceId: string, release: string, dbName: string): Promise<boolean | null> {
    try {
      const storage = getStorageAdapter(dbName);
      await storage.init();
      const existing = await storage.getStudent(release, serviceId);
      if (!existing) {
        return false;
      }
      // A record awaiting PIN setup still needs the create-and-confirm flow
      return hasPinSet(existing) && !needsMigration(existing);
    } catch {
      return null;
    }
  }

  /**
   * Shared login implementation for both the initial and post-migration paths.
   */
  private async runLogin(input: StudentLoginInput, opts: RunOptions): Promise<LoginResult> {
    const { serviceId, name, pin, release, dbName } = input;

    if (opts.checkLock) {
      const lockout = checkLockout(serviceId);
      if (lockout.isLocked) {
        return { kind: 'lockout', lockoutMs: lockout.remainingMs };
      }
    }

    try {
      const storage = getStorageAdapter(dbName);
      await storage.init();
      const existingStudent = await storage.getStudent(release, serviceId);

      if (existingStudent) {
        // Existing student needing PIN setup (legacy schema or never set a PIN)
        if (needsMigration(existingStudent) || !hasPinSet(existingStudent)) {
          const credential = await createPinCredential(pin);
          const updatedStudent = completePinSetup(existingStudent, credential);
          await storage.saveStudent(updatedStudent);
          return { kind: 'pin-created', serviceId, name, release };
        }

        // Existing student with a PIN — verify it
        const check = await verifyStoredPin(pin, existingStudent);
        if (!check.valid) {
          const state = recordFailedAttempt(serviceId);
          if (state.lockoutUntil) {
            const lockoutMs = new Date(state.lockoutUntil).getTime() - Date.now();
            return { kind: 'lockout', lockoutMs };
          }
          return { kind: 'bad-pin', remaining: getRemainingAttempts(serviceId) };
        }

        clearAttemptState(serviceId);
        // Legacy unsalted hash: replace it now that we know the PIN (SEC-07)
        if (check.upgrade) {
          await storage.saveStudent(upgradePinCredential(existingStudent, check.upgrade));
        }
        return { kind: 'pin-verified', serviceId, name, release };
      }

      // New student — derive a salted PIN verifier and create the record
      const credential = await createPinCredential(pin);
      const newStudent: StudentRecord = {
        schema: SCHEMA_VERSION,
        docId: '',
        release,
        serviceId,
        name,
        attempted: 0,
        correct: 0,
        updated: new Date().toISOString(),
        pages: {},
        ...credential,
        pinCreatedAt: new Date().toISOString(),
      };
      await storage.saveStudent(newStudent);
      return { kind: 'pin-created', serviceId, name, release };
    } catch (err) {
      if (opts.surfaceMigration && err instanceof StorageFormatError) {
        return { kind: 'needs-migration', error: err };
      }
      console.error(opts.errorLabel, err);
      return { kind: 'error', message: opts.errorMessage };
    }
  }
}
